import { useRef, useEffect, useState } from 'react'
import type { Level, Tile, BoardTopology } from '../../shared/types'
import type { TilePosition } from '../utils/moveDetection'

interface BoardProps {
  puzzle: Level
  board: (Tile | null)[]
  width: number
  height: number
  maxSize?: number // max pixel dimension (for goal preview)
  interactive?: boolean // enable drag/click (default true)
  onDragPath?: (path: TilePosition[]) => void
  onCellClick?: (index: number) => void
}

// Visual constants matching iOS/Android flat renderer
const CORNER_RADIUS_RATIO = 0.18
const GAP_RATIO = 0.06
const SYMBOL_SIZE_RATIO = 0.45
const SHADOW_BLUR = 3
const SHADOW_OFFSET_Y = 2
const SHADOW_COLOR = 'rgba(0, 0, 0, 0.15)'
const BOARD_BG = '#f2f2f2'
const DISABLED_TILE_BG = '#e6e6e6'
const SYMBOL_COLOR = 'rgba(0, 0, 0, 0.65)'

// --- Geometry helpers ---

interface TileLayout {
  cx: number // center x
  cy: number // center y
  fullIndex: number
  row: number
  col: number
}

function computeLayout(puzzle: Level, tileSize: number, gap: number): {
  tiles: TileLayout[]
  boardWidth: number
  boardHeight: number
} {
  const topology = puzzle.topology ?? 'rect'
  const step = tileSize + gap
  const tiles: TileLayout[] = []

  if (topology === 'hex_odd_r') {
    const radius = tileSize * 0.5
    const stepX = Math.sqrt(3) * radius + gap
    const stepY = radius * 1.5 + gap
    for (let row = 0; row < puzzle.height; row++) {
      const rowOffset = row % 2 !== 0 ? stepX * 0.5 : 0
      for (let col = 0; col < puzzle.width; col++) {
        const fullIndex = row * puzzle.width + col
        if (puzzle.disabled_indices.includes(fullIndex)) continue
        tiles.push({
          cx: col * stepX + rowOffset + stepX * 0.5,
          cy: row * stepY + radius,
          fullIndex,
          row,
          col,
        })
      }
    }
    const maxX = Math.max(...tiles.map((t) => t.cx)) + tileSize * 0.5
    const maxY = Math.max(...tiles.map((t) => t.cy)) + tileSize * 0.5
    return { tiles, boardWidth: maxX, boardHeight: maxY }
  }

  if (topology === 'hex_odd_q') {
    const radius = tileSize * 0.5
    const stepX = radius * 1.5 + gap
    const stepY = Math.sqrt(3) * radius + gap
    for (let row = 0; row < puzzle.height; row++) {
      for (let col = 0; col < puzzle.width; col++) {
        const fullIndex = row * puzzle.width + col
        if (puzzle.disabled_indices.includes(fullIndex)) continue
        const colOffset = col % 2 !== 0 ? stepY * 0.5 : 0
        tiles.push({
          cx: col * stepX + radius,
          cy: row * stepY + colOffset + stepY * 0.5,
          fullIndex,
          row,
          col,
        })
      }
    }
    const maxX = Math.max(...tiles.map((t) => t.cx)) + tileSize * 0.5
    const maxY = Math.max(...tiles.map((t) => t.cy)) + tileSize * 0.5
    return { tiles, boardWidth: maxX, boardHeight: maxY }
  }

  // rect (default) and graph fallback to grid
  for (let row = 0; row < puzzle.height; row++) {
    for (let col = 0; col < puzzle.width; col++) {
      const fullIndex = row * puzzle.width + col
      tiles.push({
        cx: col * step + tileSize * 0.5,
        cy: row * step + tileSize * 0.5,
        fullIndex,
        row,
        col,
      })
    }
  }
  const boardWidth = puzzle.width * step - gap
  const boardHeight = puzzle.height * step - gap
  return { tiles, boardWidth, boardHeight }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function hexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) {
  ctx.beginPath()
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (i * 60 - 30)
    const px = cx + radius * Math.cos(angle)
    const py = cy + radius * Math.sin(angle)
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  tileSize: number,
  angleDeg: number,
) {
  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate((angleDeg * Math.PI) / 180)

  const s = tileSize * 0.38
  const shaftW = s * 0.12
  const headW = s * 0.35
  const headH = s * 0.35

  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.beginPath()
  // Shaft
  ctx.moveTo(-shaftW, s * 0.4)
  ctx.lineTo(shaftW, s * 0.4)
  ctx.lineTo(shaftW, -s * 0.4 + headH)
  // Arrowhead
  ctx.lineTo(headW, -s * 0.4 + headH)
  ctx.lineTo(0, -s * 0.4)
  ctx.lineTo(-headW, -s * 0.4 + headH)
  ctx.lineTo(-shaftW, -s * 0.4 + headH)
  ctx.closePath()
  ctx.fill()

  ctx.restore()
}

export default function Board({ puzzle, board, maxSize, interactive = true, onDragPath, onCellClick }: BoardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragPath, setDragPath] = useState<TilePosition[]>([]) // tile indices for move detection
  const [pixelPath, setPixelPath] = useState<{ x: number; y: number }[]>([]) // raw pointer positions for trail
  const layoutRef = useRef<{ tiles: TileLayout[]; padding: number; tileSize: number } | null>(null)

  const topology: BoardTopology = puzzle.topology ?? 'rect'
  const isHex = topology === 'hex_odd_r' || topology === 'hex_odd_q'

  // Find the closest tile to a canvas point
  const getTileFromPoint = (x: number, y: number): TilePosition | null => {
    const layout = layoutRef.current
    if (!layout) return null

    const { tiles, padding, tileSize } = layout
    const hitRadius = tileSize * 0.5

    for (const t of tiles) {
      const fullIndex = t.fullIndex
      // Skip disabled cells
      if (puzzle.disabled_indices.includes(fullIndex)) continue
      // Skip holes (null board entries)
      if (board[fullIndex] === null) continue

      const dx = x - (padding + t.cx)
      const dy = y - (padding + t.cy)
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist <= hitRadius) {
        return { row: t.row, col: t.col }
      }
    }
    return null
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    const tile = getTileFromPoint(x, y)
    if (tile) {
      setIsDragging(true)
      setDragPath([tile])
      setPixelPath([{ x, y }])
    }
  }

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    setPixelPath((prev) => [...prev, { x, y }])
    const tile = getTileFromPoint(x, y)
    if (tile) {
      const lastTile = dragPath[dragPath.length - 1]
      if (!lastTile || lastTile.row !== tile.row || lastTile.col !== tile.col) {
        setDragPath((prev) => [...prev, tile])
      }
    }
  }

  const handleMouseUp = () => {
    if (isDragging && dragPath.length >= 2 && onDragPath) {
      onDragPath(dragPath)
    }
    setIsDragging(false)
    setDragPath([])
    setPixelPath([])
  }

  const handleMouseLeave = () => {
    if (isDragging) handleMouseUp()
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const maxWidth = maxSize ?? Math.min(window.innerWidth - 100, 800)
    const maxHeight = maxSize ?? Math.min(window.innerHeight - 200, 800)

    // Binary search for largest tile size that fits
    const computeFit = (ts: number) => {
      const g = Math.round(ts * GAP_RATIO)
      const { boardWidth, boardHeight } = computeLayout(puzzle, ts, g)
      return { fits: boardWidth <= maxWidth && boardHeight <= maxHeight, boardWidth, boardHeight }
    }

    let lo = 10, hi = 200
    while (lo < hi) {
      const mid = Math.ceil((lo + hi + 1) / 2)
      if (computeFit(mid).fits) lo = mid
      else hi = mid - 1
    }
    const tileSize = lo
    const gap = Math.round(tileSize * GAP_RATIO)
    const cornerRadius = Math.round(tileSize * CORNER_RADIUS_RATIO)
    const { tiles, boardWidth, boardHeight } = computeLayout(puzzle, tileSize, gap)
    const padding = Math.round(gap * 1.5)

    // Store layout for hit testing
    layoutRef.current = { tiles, padding, tileSize }

    const canvasW = boardWidth + padding * 2
    const canvasH = boardHeight + padding * 2

    canvas.width = canvasW * dpr
    canvas.height = canvasH * dpr
    canvas.style.width = `${canvasW}px`
    canvas.style.height = `${canvasH}px`
    ctx.scale(dpr, dpr)

    // Board background
    const boardBgRadius = Math.round(cornerRadius * 0.8)
    ctx.fillStyle = BOARD_BG
    roundRect(ctx, padding * 0.5, padding * 0.5, boardWidth + padding, boardHeight + padding, boardBgRadius)
    ctx.fill()

    // Draw each tile
    for (const tl of tiles) {
      const tile = board[tl.fullIndex]
      const cx = padding + tl.cx
      const cy = padding + tl.cy
      const half = tileSize * 0.5

      if (tile) {
        // Shadow
        ctx.save()
        ctx.shadowColor = SHADOW_COLOR
        ctx.shadowBlur = SHADOW_BLUR
        ctx.shadowOffsetX = 0
        ctx.shadowOffsetY = SHADOW_OFFSET_Y
        ctx.fillStyle = tile.color

        if (isHex) {
          hexPath(ctx, cx, cy, half * 0.92)
        } else {
          roundRect(ctx, cx - half, cy - half, tileSize, tileSize, cornerRadius)
        }
        ctx.fill()
        ctx.restore()

        // Symbol and orientation
        const displayValue = tile.values[0]
        const entry = puzzle.palette.mappings[String(displayValue)]
        const indicator = entry?.orientation_indicator ?? 'none'
        const numOrients = puzzle.num_orientations_map[String(displayValue)] ?? 1
        const orientation = tile.orientations[0] ?? 0

        if (tile.symbol) {
          if (indicator === 'rotate_symbol' && numOrients > 1) {
            const angleDeg = (orientation * 360) / numOrients
            ctx.save()
            ctx.translate(cx, cy)
            ctx.rotate((angleDeg * Math.PI) / 180)
            ctx.fillStyle = SYMBOL_COLOR
            ctx.font = `bold ${Math.floor(tileSize * SYMBOL_SIZE_RATIO)}px "Menlo", "SF Mono", "Consolas", monospace`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(tile.symbol, 0, 0)
            ctx.restore()
          } else {
            ctx.fillStyle = SYMBOL_COLOR
            ctx.font = `bold ${Math.floor(tileSize * SYMBOL_SIZE_RATIO)}px "Menlo", "SF Mono", "Consolas", monospace`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(tile.symbol, cx, cy)
          }
        }

        // Orientation arrow (independent of symbol)
        if (indicator === 'arrow' && numOrients > 1) {
          const angleDeg = (orientation * 360) / numOrients
          drawArrow(ctx, cx, cy, tileSize, angleDeg)
        }
      } else if (!puzzle.disabled_indices.includes(tl.fullIndex)) {
        // Non-disabled empty cell (shouldn't happen, but handle gracefully)
        ctx.fillStyle = DISABLED_TILE_BG
        if (isHex) {
          hexPath(ctx, cx, cy, half * 0.92)
        } else {
          roundRect(ctx, cx - half, cy - half, tileSize, tileSize, cornerRadius)
        }
        ctx.fill()
      }
      // Disabled cells: don't draw anything (they're excluded from tiles for hex,
      // but for rect they're in the list — skip rendering)
    }

    // Drag trail - freeform path following pointer (matching iOS)
    if (isDragging && pixelPath.length > 1) {
      const drawLine = (color: string, lineWidth: number) => {
        ctx.strokeStyle = color
        ctx.lineWidth = lineWidth
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.beginPath()
        ctx.moveTo(pixelPath[0].x, pixelPath[0].y)
        for (let i = 1; i < pixelPath.length; i++) {
          ctx.lineTo(pixelPath[i].x, pixelPath[i].y)
        }
        ctx.stroke()
      }

      // Outer glow
      drawLine('rgba(0, 188, 212, 0.25)', Math.max(16, tileSize * 0.18))
      // Mid glow
      drawLine('rgba(0, 188, 212, 0.4)', Math.max(10, tileSize * 0.12))
      // Core trail
      drawLine('rgba(0, 220, 255, 0.7)', Math.max(4, tileSize * 0.07))

      // Shimmer dots sampled along the path
      const dotInterval = Math.max(1, Math.floor(pixelPath.length / 20))
      for (let i = 0; i < pixelPath.length; i += dotInterval) {
        const opacity = 0.5 + (i / Math.max(1, pixelPath.length - 1)) * 0.5
        const p = pixelPath[i]
        const dotR = Math.max(5, tileSize * 0.05)

        ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.9})`
        ctx.beginPath()
        ctx.arc(p.x, p.y, dotR * 0.5, 0, Math.PI * 2)
        ctx.fill()

        ctx.fillStyle = `rgba(0, 220, 255, ${opacity * 0.6})`
        ctx.beginPath()
        ctx.arc(p.x, p.y, dotR, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }, [puzzle, board, isDragging, dragPath, pixelPath])

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onCellClick) return
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    const tile = getTileFromPoint(x, y)
    if (tile) {
      const index = tile.row * puzzle.width + tile.col
      onCellClick(index)
    }
  }

  if (!interactive) {
    return <canvas ref={canvasRef} />
  }

  return (
    <div className="flex items-center justify-center p-8">
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className="max-w-full max-h-full cursor-pointer"
      />
    </div>
  )
}
